/**
 * BdcTeam — Slack-lite chat surface for the BDC team.
 *
 * ── Layout ────────────────────────────────────────────────────────────
 *   Left column (~260px): channel list. "BDC Team" pinned to top, then
 *   DMs sorted by ``last_message_at`` descending. Each row shows unread
 *   count + preview + presence dot (for DMs only).
 *   Right column (1fr): active channel view with header (member avatars),
 *   scrollable message list, and a composer at the bottom.
 *
 * ── Polling ───────────────────────────────────────────────────────────
 *   Messages poll every 3s (matches the existing customer chat cadence).
 *   Channel list refreshes every 15s. When a channel is opened its
 *   ``last_read_at`` cursor advances on the server, dropping the unread
 *   badge in the left column on next fetch.
 *
 * ── Query param ───────────────────────────────────────────────────────
 *   `?channel=<id>` selects a channel on mount. Presence sidebar rows
 *   use this to hop straight into a DM (idempotent — POST /bdc/team/dm
 *   returns existing channel id when there is one).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Hash, Loader2, MessageCircle, Send, User as UserIcon } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const MSG_POLL_MS = 3000;
const CHANNELS_POLL_MS = 15_000;

const fmtRelative = (iso) => {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const diff = Date.now() - then;
  if (diff < 60_000) return "just now";
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h`;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

const initials = (name) => {
  const parts = (name || "").split(" ").filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const BdcTeam = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialChannelId = searchParams.get("channel");

  const [channels, setChannels] = useState([]);
  const [activeId, setActiveId] = useState(initialChannelId || null);
  const [messages, setMessages] = useState([]);
  const [composer, setComposer] = useState("");
  const [sending, setSending] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const messagesEndRef = useRef(null);

  const activeChannel = useMemo(
    () => channels.find((c) => c.id === activeId) || null,
    [channels, activeId],
  );

  const loadChannels = useCallback(async () => {
    try {
      const { data } = await api.get("/bdc/team/channels");
      setChannels(data);
      // Auto-select the team channel on first mount if nothing selected yet.
      if (!activeId && data.length) {
        const team = data.find((c) => c.channel_type === "team") || data[0];
        setActiveId(team.id);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [activeId]);

  useEffect(() => {
    loadChannels();
    const t = setInterval(loadChannels, CHANNELS_POLL_MS);
    return () => clearInterval(t);
  }, [loadChannels]);

  // Message polling — cursor-based so we don't re-fetch the world every 3s.
  const cursorRef = useRef(null);
  useEffect(() => {
    if (!activeId) return;
    setMessages([]);
    cursorRef.current = null;
    setLoadingMessages(true);

    let cancelled = false;
    const tick = async () => {
      try {
        const params = cursorRef.current ? { after: cursorRef.current } : {};
        const { data } = await api.get(`/bdc/team/channels/${activeId}/messages`, { params });
        if (cancelled) return;
        if (data.messages?.length) {
          setMessages((prev) => {
            // De-dup by id in case of overlap.
            const seen = new Set(prev.map((m) => m.id));
            const fresh = data.messages.filter((m) => !seen.has(m.id));
            const merged = [...prev, ...fresh];
            cursorRef.current = merged[merged.length - 1]?.created_at;
            return merged;
          });
        }
      } catch (e) {
        // Silent on poll — surface only once.
        if (!cursorRef.current) toast.error(formatApiError(e));
      } finally {
        if (!cancelled) setLoadingMessages(false);
      }
    };
    tick();
    const t = setInterval(tick, MSG_POLL_MS);
    return () => { cancelled = true; clearInterval(t); };
  }, [activeId]);

  // Auto-scroll to bottom when new messages arrive.
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages]);

  const send = async () => {
    const body = composer.trim();
    if (!body || !activeId || sending) return;
    setSending(true);
    try {
      const { data } = await api.post(`/bdc/team/channels/${activeId}/messages`, { body });
      // Optimistic-ish: append to local list. Poll will reconcile if any race.
      setMessages((prev) => (prev.some((m) => m.id === data.id) ? prev : [...prev, data]));
      cursorRef.current = data.created_at;
      setComposer("");
      // Nudge channel list so preview updates without waiting for the 15s poll.
      loadChannels();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  const selectChannel = (cid) => {
    setActiveId(cid);
    setSearchParams({ channel: cid }, { replace: true });
  };

  const totalUnread = channels.reduce((n, c) => n + (c.unread_count || 0), 0);

  return (
    <div className="h-[calc(100vh-4rem)] lg:h-screen flex bg-white" data-testid="bdc-team-page">
      {/* Channel list */}
      <aside className="w-64 shrink-0 border-r border-slate-200 flex flex-col bg-slate-50">
        <div className="px-4 py-4 border-b border-slate-200">
          <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Team chat</p>
          <p className="font-display text-lg font-semibold text-slate-900 mt-0.5">
            Messages
            {totalUnread > 0 && (
              <span className="ml-2 rounded-full bg-brand text-white text-[10px] font-bold px-2 py-0.5 align-middle">{totalUnread}</span>
            )}
          </p>
        </div>
        <div className="flex-1 overflow-y-auto p-2 space-y-1" data-testid="bdc-team-channel-list">
          {channels.map((c) => {
            const isActive = c.id === activeId;
            const isTeam = c.channel_type === "team";
            const displayName = isTeam ? c.name : c.other_member?.full_name || c.name;
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => selectChannel(c.id)}
                className={`w-full text-left rounded-lg px-3 py-2.5 transition-colors ${
                  isActive ? "bg-white border border-slate-200 shadow-sm" : "hover:bg-white/70"
                }`}
                data-testid={`bdc-team-channel-${c.id}`}
              >
                <div className="flex items-center gap-2">
                  {isTeam ? (
                    <Hash size={13} className="text-slate-400 shrink-0" />
                  ) : (
                    <span className="w-5 h-5 rounded-full bg-slate-200 text-slate-700 text-[10px] font-semibold flex items-center justify-center shrink-0">
                      {initials(displayName)}
                    </span>
                  )}
                  <span className="font-semibold text-sm text-slate-900 truncate flex-1">{displayName}</span>
                  {c.unread_count > 0 && (
                    <span className="rounded-full bg-brand text-white text-[10px] font-bold px-1.5 min-w-[18px] text-center">
                      {c.unread_count}
                    </span>
                  )}
                </div>
                {c.last_message_preview && (
                  <p className="mt-0.5 pl-6 text-[11px] text-slate-500 truncate">{c.last_message_preview}</p>
                )}
                {c.last_message_at && (
                  <p className="mt-0.5 pl-6 text-[10px] text-slate-400">{fmtRelative(c.last_message_at)}</p>
                )}
              </button>
            );
          })}
          {!channels.length && (
            <p className="px-3 py-4 text-xs text-slate-400 italic">Loading channels…</p>
          )}
        </div>
      </aside>

      {/* Active channel */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="h-16 border-b border-slate-200 px-6 flex items-center justify-between shrink-0">
          {activeChannel ? (
            <>
              <div className="flex items-center gap-3 min-w-0">
                {activeChannel.channel_type === "team" ? (
                  <Hash size={16} className="text-slate-400" />
                ) : (
                  <MessageCircle size={16} className="text-slate-400" />
                )}
                <div className="min-w-0">
                  <p className="font-display font-semibold text-slate-900 truncate">
                    {activeChannel.channel_type === "team" ? activeChannel.name : activeChannel.other_member?.full_name || activeChannel.name}
                  </p>
                  <p className="text-[11px] text-slate-500">
                    {activeChannel.channel_type === "team"
                      ? `${activeChannel.member_ids?.length || 0} members`
                      : "Direct message"}
                  </p>
                </div>
              </div>
              {loadingMessages && <Loader2 size={14} className="animate-spin text-slate-400" />}
            </>
          ) : (
            <p className="text-sm text-slate-400">Select a channel…</p>
          )}
        </header>

        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-4" data-testid="bdc-team-messages">
          {messages.map((m) => {
            const mine = m.sender_bdc_id === user?.id;
            return (
              <div
                key={m.id}
                className={`flex items-start gap-3 ${mine ? "flex-row-reverse" : ""}`}
                data-testid={`bdc-team-message-${m.id}`}
              >
                <span className="w-8 h-8 rounded-full bg-slate-200 text-slate-700 text-xs font-semibold flex items-center justify-center shrink-0">
                  {initials(m.sender_name)}
                </span>
                <div className={`max-w-[70%] rounded-2xl px-4 py-2.5 ${
                  mine ? "bg-brand text-white" : "bg-slate-100 text-slate-900"
                }`}>
                  <p className={`text-[10px] font-semibold ${mine ? "text-white/70" : "text-slate-500"} mb-0.5`}>
                    {mine ? "You" : m.sender_name} · {fmtRelative(m.created_at)}
                  </p>
                  <p className="text-sm whitespace-pre-wrap break-words">{m.body}</p>
                </div>
              </div>
            );
          })}
          {!messages.length && !loadingMessages && (
            <p className="text-xs text-slate-400 italic text-center py-8">No messages yet — say hi.</p>
          )}
          <div ref={messagesEndRef} />
        </div>

        <footer className="border-t border-slate-200 p-4 shrink-0">
          <div className="flex items-center gap-2">
            <Input
              value={composer}
              onChange={(e) => setComposer(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              placeholder={activeChannel ? `Message ${activeChannel.channel_type === "team" ? activeChannel.name : (activeChannel.other_member?.full_name || "…")}` : "Select a channel first"}
              disabled={!activeChannel || sending}
              data-testid="bdc-team-composer"
            />
            <Button
              onClick={send}
              disabled={!composer.trim() || !activeChannel || sending}
              className="bg-brand text-white hover:bg-brand-hover"
              data-testid="bdc-team-send-btn"
            >
              {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              Send
            </Button>
          </div>
          <p className="mt-1.5 text-[10px] text-slate-400">
            <UserIcon size={9} className="inline" /> Only BDC agents can read these messages. Rate-limited to 60/min per agent.
          </p>
        </footer>
      </div>
    </div>
  );
};

export default BdcTeam;
